import { createField, type ScalarField } from './grid.ts';

/**
 * Initial ("existing") snow cover before any scenario events run.
 *
 * Depth varies linearly with elevation from a reference point, is zero below the snow line
 * (with a short taper so the edge isn't a hard step), and thins out on steep faces that can't
 * hold snow. Depth is height of snow measured vertically, in metres.
 */
export interface InitialSnowParams {
  /** Snow depth at `referenceElevation` (m). */
  referenceDepth: number;
  referenceElevation: number;
  /** Depth change per 100 m of elevation (m). */
  gradientPer100m: number;
  /** No snow below this elevation (m). */
  snowlineElevation: number;
  /** Bulk density of the existing snowpack (kg/m³). */
  density: number;
  /** Slope angle (deg) where snow starts to shed. */
  shedStartDeg: number;
  /** Slope angle (deg) at and above which no snow stays. */
  shedEndDeg: number;
}

export const DEFAULT_INITIAL_SNOW: InitialSnowParams = {
  referenceDepth: 1.0,
  referenceElevation: 500,
  gradientPer100m: 0.1,
  snowlineElevation: 100,
  density: 300,
  shedStartDeg: 45,
  shedEndDeg: 60,
};

/** Width of the linear taper above the snow line (m of elevation). */
export const SNOWLINE_TAPER_M = 50;

export function snowDepthAt(p: InitialSnowParams, elevation: number, slopeDeg: number): number {
  let depth = p.referenceDepth + ((elevation - p.referenceElevation) / 100) * p.gradientPer100m;
  if (depth <= 0) return 0;
  const aboveSnowline = elevation - p.snowlineElevation;
  if (aboveSnowline <= 0) return 0;
  if (aboveSnowline < SNOWLINE_TAPER_M) depth *= aboveSnowline / SNOWLINE_TAPER_M;
  return depth * steepSlopeFactor(p, slopeDeg);
}

/** 1 below `shedStartDeg`, 0 at/above `shedEndDeg`, linear in between. */
export function steepSlopeFactor(p: InitialSnowParams, slopeDeg: number): number {
  if (slopeDeg <= p.shedStartDeg) return 1;
  if (slopeDeg >= p.shedEndDeg) return 0;
  return (p.shedEndDeg - slopeDeg) / (p.shedEndDeg - p.shedStartDeg);
}

export function initialSnowDepth(
  p: InitialSnowParams,
  elevation: ScalarField,
  slope: ScalarField,
): ScalarField {
  const out = createField(elevation.spec);
  for (let i = 0; i < out.data.length; i++) {
    out.data[i] = snowDepthAt(p, elevation.data[i]!, slope.data[i]!);
  }
  return out;
}

/** Snow water equivalent (mm = kg/m²) for a depth (m) and bulk density (kg/m³). */
export function sweMm(depth: number, density: number): number {
  return depth * density;
}
